import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';

export interface UserPermissions {
  canCreateGames: boolean;
  canManageTeams: boolean;
  canManageVenues: boolean;
  canModerateContent: boolean;
  isCoach: boolean;
  isTeamOwner: boolean;
  isVenue: boolean;
  isPlayer: boolean;
  role: string;
  userType?: 'supabase' | 'internal';
}

export const useUserRole = () => {
  const [permissions, setPermissions] = useState<UserPermissions>({
    canCreateGames: false,
    canManageTeams: false,
    canManageVenues: false,
    canModerateContent: false,
    isCoach: false,
    isTeamOwner: false,
    isVenue: false,
    isPlayer: false,
    role: 'player'
  });
  
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  
  // Prioritize internal user
  const currentUser = internalUser || supabaseUser;
  const userType: 'supabase' | 'internal' | null = internalUser ? 'internal' : (supabaseUser ? 'supabase' : null);

  useEffect(() => {
    if (!currentUser) {
      setPermissions({
        canCreateGames: false,
        canManageTeams: false,
        canManageVenues: false,
        canModerateContent: false,
        isCoach: false,
        isTeamOwner: false,
        isVenue: false,
        isPlayer: false,
        role: 'player'
      });
      return;
    }

    // For internal users, use their role directly
    const userRole = internalUser?.role || 'player';

    setPermissions({
      canCreateGames: ['coach', 'team-owner', 'venue', 'admin'].includes(userRole),
      canManageTeams: ['coach', 'team-owner', 'admin'].includes(userRole),
      canManageVenues: ['venue', 'admin'].includes(userRole),
      canModerateContent: ['admin'].includes(userRole),
      isCoach: userRole === 'coach',
      isTeamOwner: userRole === 'team-owner',
      isVenue: userRole === 'venue',
      isPlayer: userRole === 'player',
      role: userRole,
      userType: userType || undefined
    });
  }, [currentUser, internalUser, userType]);

  return {
    permissions,
    currentUser,
    userId: currentUser?.id,
    userType
  };
};